import { z } from "zod";
import type { PointRuleInput } from "@core/types";

// Mensajes literales de SPEC-212 §9 (tabla de "Mensajes al usuario"): son el
// contrato de UX, no una redacción libre — el servidor devuelve el mismo
// código si el cliente los saltea (A3, human-resolution-spec-212.json).
export const VALIDITY_REQUIRED_MESSAGE =
  "La regla requiere fecha de inicio de vigencia";
export const CAP_INCOMPLETE_MESSAGE =
  "Indica la cantidad y la frecuencia, o deja la regla sin tope";
// El servicio (api/src/management/point-rules/point-rules.repository.ts,
// `OverlappingValidityError`) no transporta las fechas de la regla que
// estorba — es un código sin datos adicionales (deuda de contrato, igual
// clase que A1/A4 de human-resolution-spec-212.json). El texto literal de
// la sección 9 se muestra tal cual, sin inventar fechas que el servidor no
// envía.
export const OVERLAPPING_VALIDITY_MESSAGE =
  "Ya existe una regla activa para esta acción entre el DD/MM y el DD/MM";
// T7 (qa-bug-matrix-2026-09.md §5.4, registro único a tuteo): "¿Confirmas?"
// coincide carácter por carácter con la tabla de la sección 9 de SPEC-212
// (`:154`), que ya estaba en tuteo — era la prosa de "Decisiones pendientes"
// la que traía el voseo de más, sin ser la fuente.
export const CONFIRM_INACTIVATE_MESSAGE =
  "Esta regla dejará de otorgar puntos de inmediato. ¿Confirmas?";

// SPEC-212 §3.1, texto literal de ayuda del tope por período (A2,
// human-resolution-spec-212.json): ocupa la ranura `hint` del `Field`
// compartido cuando hay tope; con "Sin tope" se conserva "No aplica", el
// mismo hint condicional que ya traía el prototipo (admin.jsx:3186-3188).
export const CAP_PERIOD_HINT =
  'Cuántas veces esta acción puede otorgar puntos a un mismo miembro dentro del período. Ejemplo: "3 por mes" significa que el cuarto registro del mismo mes se rechaza: no se crea ningún movimiento, tampoco de cero puntos. El período se cuenta por mes o año calendario: un tope mensual se reinicia el día 1.';

// QA-MTZ-002 #11: con Mes/Año el tope debe ser un entero positivo; antes el
// rechazo salía con el texto por defecto de zod, en inglés («Invalid
// input»). El máximo es el de la columna `smallint` de
// `point_rule.cap_quantity`, que la API también valida por campo.
export const CAP_POSITIVE_MESSAGE =
  "El tope por período debe ser un número entero mayor que cero";
export const CAP_MAX = 32767;
export const CAP_MAX_MESSAGE = `El tope por período no puede ser mayor que ${String(CAP_MAX)}`;
export const POINTS_REQUIRED_MESSAGE = "Indique la cantidad de puntos";
export const POINTS_INTEGER_MESSAGE =
  "Los puntos deben ser un número entero mayor que cero";

export const CAP_FREQUENCIES = ["Sin tope", "Mes", "Año"] as const;
export type CapFrequencyLabel = (typeof CAP_FREQUENCIES)[number];

const capFrequencyToWire: Record<
  Exclude<CapFrequencyLabel, "Sin tope">,
  "month" | "year"
> = { Mes: "month", Año: "year" };
const capFrequencyFromWire: Record<"month" | "year", CapFrequencyLabel> = {
  month: "Mes",
  year: "Año",
};

export const ruleFormSchema = z
  .object({
    actionId: z.string().min(1, "La acción es obligatoria"),
    points: z
      .number({ error: POINTS_REQUIRED_MESSAGE })
      .int(POINTS_INTEGER_MESSAGE)
      .positive(POINTS_REQUIRED_MESSAGE),
    capFrequencyLabel: z.enum(CAP_FREQUENCIES),
    // QA-MTZ-002 #11: sin reglas propias acá — con «Sin tope» el tope no
    // aplica y no se valida (el input está deshabilitado y react-hook-form
    // lo entrega como `undefined`); con Mes/Año lo valida el `superRefine`
    // de abajo, con mensajes en español. `z.custom` y no `z.number()`: este
    // último rechaza `NaN` con su texto en inglés aun con «Sin tope».
    capQuantity: z.custom<number | null | undefined>(
      (value) =>
        value === null || value === undefined || typeof value === "number",
      { error: CAP_POSITIVE_MESSAGE },
    ),
    validFrom: z.string().min(1, VALIDITY_REQUIRED_MESSAGE),
    validUntil: z.string(),
    assignment: z.enum(["automatic", "manual"]),
    active: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (values.capFrequencyLabel === "Sin tope") return;
    const quantity = values.capQuantity;
    const message =
      quantity === null || quantity === undefined
        ? CAP_INCOMPLETE_MESSAGE
        : !Number.isInteger(quantity) || quantity <= 0
          ? CAP_POSITIVE_MESSAGE
          : quantity > CAP_MAX
            ? CAP_MAX_MESSAGE
            : null;
    if (message !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ["capQuantity"],
      });
    }
  });
export type RuleFormValues = z.infer<typeof ruleFormSchema>;

export function defaultRuleFormValues(actionId = ""): RuleFormValues {
  // A1 (human-resolution-spec-212.json): el formulario propone "Sin tope",
  // igual que el prototipo (admin.jsx:3111, `BLANK_REGLA`) — no hay ruta
  // HTTP para `points_engine.default_cap`/`default_frequency`, así que no
  // se simula ningún valor por defecto.
  return {
    actionId,
    points: 0,
    capFrequencyLabel: "Sin tope",
    capQuantity: null,
    validFrom: "",
    validUntil: "",
    assignment: "automatic",
    active: true,
  };
}

export function buildRuleInput(values: RuleFormValues): PointRuleInput {
  const base: PointRuleInput = {
    actionId: values.actionId,
    points: values.points,
    validFrom: values.validFrom,
    validUntil: values.validUntil || null,
    assignment: values.assignment,
    active: values.active,
  };
  // known_defect (api-http, hallado 2026-09-07 contra el backend real): el
  // contrato declara capQuantity/capFrequency `nullable: true`
  // (api/contracts/parts/20-members-points.yaml:1167-1168), pero el
  // validador de la API rechaza con 400 INVALID_REQUEST un payload que los
  // manda en null; omitir ambas claves (también legal, son opcionales) sí
  // funciona. 7/7 POST con null fallaron, 7/7 con las claves omitidas
  // entraron con 201 — reproducido contra la API sembrada de esta corrida.
  // No se toca api/ ni el contrato: el desajuste queda anotado para
  // api-http, no se trabaja alrededor con un valor inventado.
  if (values.capFrequencyLabel === "Sin tope") return base;
  return {
    ...base,
    capQuantity: values.capQuantity ?? null,
    capFrequency: capFrequencyToWire[values.capFrequencyLabel],
  };
}

export function capFrequencyLabelOf(
  capFrequency: "month" | "year" | null | undefined,
): CapFrequencyLabel {
  if (!capFrequency) return "Sin tope";
  return capFrequencyFromWire[capFrequency];
}
